/* ==========================================================================
   Blinq · Product (sections/blinq-product.liquid)
   Gallery (arrows, swipe, thumbnails, videos), colour / option picker,
   price + Add to cart state, quantity. One init per section on the page;
   runs again when the theme editor reloads the section.
   ========================================================================== */
(() => {
  const init = (root) => {
  if (!root || root.dataset.bprodReady) return;
  root.dataset.bprodReady = 'true';

  let variants = [];
  try { variants = JSON.parse(root.querySelector('[data-bprod-variants]').textContent); } catch (e) {}

  const idInput = root.querySelector('[data-bprod-variant-id]');
  const planInput = root.querySelector('[data-bprod-selling-plan]'); /* pre-order plan (Ace only) */
  const atc = root.querySelector('[data-bprod-atc]');
  const atcText = atc && atc.querySelector('[data-sold-out]');
  const mainImg = root.querySelector('[data-bprod-main] img');
  const colorsBox = root.querySelector('[data-bprod-colors]');
  const colorToggle = root.querySelector('[data-bprod-colors-toggle]');
  const optionSets = [...root.querySelectorAll('[data-bprod-option]')];
  const priceEls = [root.querySelector('[data-bprod-price]'), root.querySelector('[data-bprod-price-mobile]')].filter(Boolean);
  const showCode = root.dataset.showCode === 'true';
  const code = root.dataset.currency || '';

  const selectedOptions = () =>
    optionSets
      .sort((a, b) => a.dataset.bprodOption - b.dataset.bprodOption)
      .map((set) => (set.querySelector('input:checked') || {}).value);

  const findVariant = (opts) =>
    variants.find((v) => v.options.every((o, i) => o === opts[i]));

  /* ---- Main area: image or video ---- */
  const videos = [...root.querySelectorAll('[data-bprod-video]')];
  const mediaVideos = [...root.querySelectorAll('[data-bprod-media-video]')];
  const hideVideos = () => {
    videos.forEach((wrap) => {
      const vid = wrap.querySelector('video');
      if (vid) vid.pause();
      wrap.hidden = true;
    });
    /* Product Media videos: pause, and drop YouTube / Vimeo players so they stop */
    mediaVideos.forEach((wrap) => {
      const vid = wrap.querySelector('video');
      if (vid) vid.pause();
      if (wrap.querySelector('iframe')) wrap.innerHTML = '';
      wrap.hidden = true;
    });
    const box = root.querySelector('[data-bprod-main]');
    if (box) box.classList.remove('is-external');
  };
  /* Show a video from the product's Media in the main area (added from its template on first view) */
  const showMediaVideo = (id) => {
    const wrap = mediaVideos.find((w) => w.dataset.bprodMediaVideo === String(id));
    if (!wrap) return false;
    hideVideos();
    if (!wrap.childElementCount) {
      const tpl = root.querySelector(`template[data-bprod-media-template="${id}"]`);
      if (tpl) wrap.appendChild(tpl.content.cloneNode(true));
    }
    wrap.hidden = false;
    if (mainImg) mainImg.classList.add('is-hidden');
    const box = root.querySelector('[data-bprod-main]');
    if (box) box.classList.toggle('is-external', !!wrap.querySelector('iframe'));
    const vid = wrap.querySelector('video');
    if (vid) {
      vid.muted = true;
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      const p = vid.play();
      if (p && p.catch) p.catch(() => {});
    }
    return true;
  };
  const showMedia = (src, srcset, alt) => {
    hideVideos();
    if (!mainImg) return;
    mainImg.classList.remove('is-hidden');
    if (!src) return;
    mainImg.classList.add('is-loading');
    const done = () => mainImg.classList.remove('is-loading');
    mainImg.addEventListener('load', done, { once: true });
    if (srcset) mainImg.srcset = srcset; else mainImg.removeAttribute('srcset');
    mainImg.src = src;
    if (alt != null) mainImg.alt = alt;
    if (mainImg.complete) done();
  };
  const showVideo = (id) => {
    const wrap = videos.find((w) => w.dataset.bprodVideo === id);
    if (!wrap) return;
    hideVideos();
    videos.forEach((w) => { if (w !== wrap) { const v = w.querySelector('video'); if (v) v.pause(); w.hidden = true; } });
    wrap.hidden = false;
    if (mainImg) mainImg.classList.add('is-hidden');
    const vid = wrap.querySelector('video');
    if (vid) {
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      const p = vid.play();
      if (p && p.catch) p.catch(() => {});
    }
  };
  /* Thumbnail carousel: keep the selected thumbnail in view, show arrows only when there's more to see */
  const track = root.querySelector('[data-bprod-carousel-track]');
  const carouselArrows = [...root.querySelectorAll('[data-bprod-carousel-arrow]')];
  const updateCarouselArrows = () => {
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    carouselArrows.forEach((a) => {
      a.hidden = Number(a.dataset.bprodCarouselArrow) < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max;
    });
  };
  const keepInView = (btn, behavior = 'smooth') => {
    if (!track || !btn || !track.contains(btn)) return;
    const t = track.getBoundingClientRect();
    const b = btn.getBoundingClientRect();
    if (b.left < t.left + 3) track.scrollBy({ left: b.left - t.left - 3, behavior });
    else if (b.right > t.right - 3) track.scrollBy({ left: b.right - t.right + 3, behavior });
  };
  if (track) {
    carouselArrows.forEach((a) =>
      a.addEventListener('click', () =>
        track.scrollBy({ left: Number(a.dataset.bprodCarouselArrow) * track.clientWidth * 0.75, behavior: 'smooth' })
      )
    );
    track.addEventListener('scroll', updateCarouselArrows, { passive: true });
    window.addEventListener('resize', updateCarouselArrows);
    updateCarouselArrows();
    keepInView(track.querySelector('.is-active'), 'auto');
  }

  const setActiveThumb = (btn) => {
    root.querySelectorAll('[data-bprod-thumb]').forEach((t) => {
      const on = t === btn;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', String(on));
    });
    keepInView(btn);
  };

  /* ---- Main image slider: the product's own images, in admin order ---- */
  let slides = [];
  try { slides = JSON.parse((root.querySelector('[data-bprod-slides]') || {}).textContent || '[]'); } catch (e) {}
  const mainBox = root.querySelector('[data-bprod-main]');
  const counter = root.querySelector('[data-bprod-counter]');
  let slideIndex = Math.max(0, Number(counter ? counter.textContent : 1) - 1);
  const indexOfMedia = (id) => slides.findIndex((sl) => String(sl.id) === String(id));
  const syncCounter = () => { if (counter) counter.textContent = String(slideIndex + 1); };
  /* Light up the thumbnail that shows this image, if there is one */
  const thumbForMedia = (id) =>
    root.querySelector(`[data-bprod-thumb][data-media-id="${id}"]`) ||
    ([...root.querySelectorAll('[data-bprod-thumb-variant]')].find((t) => t.dataset.mediaId === String(id)) || null);
  const goTo = (i) => {
    if (!slides.length) return;
    slideIndex = (i + slides.length) % slides.length;
    const sl = slides[slideIndex];
    if (sl.kind === 'image' || !sl.kind || !showMediaVideo(sl.id)) showMedia(sl.src, sl.srcset, sl.alt);
    if (mainBox) mainBox.classList.remove('is-video');
    setActiveThumb(thumbForMedia(sl.id));
    syncCounter();
  };
  if (slides[slideIndex] && slides[slideIndex].kind && slides[slideIndex].kind !== 'image') showMediaVideo(slides[slideIndex].id);
  root.querySelectorAll('[data-bprod-arrow]').forEach((btn) =>
    btn.addEventListener('click', () => goTo(slideIndex + Number(btn.dataset.bprodArrow)))
  );
  /* Swipe left / right on the main image (touch) */
  if (mainBox && slides.length > 1) {
    let x0 = null, y0 = null;
    mainBox.addEventListener('touchstart', (e) => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; }, { passive: true });
    mainBox.addEventListener('touchend', (e) => {
      if (x0 == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0, dy = t.clientY - y0;
      x0 = y0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) goTo(slideIndex + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  root.querySelectorAll('[data-bprod-thumb]').forEach((btn) =>
    btn.addEventListener('click', () => {
      if (btn.dataset.kind === 'video') {
        showVideo(btn.dataset.video);
        if (mainBox) mainBox.classList.add('is-video');
      } else if (btn.dataset.kind === 'media-video') {
        showMediaVideo(btn.dataset.mediaId);
        if (mainBox) mainBox.classList.remove('is-video');
        const i = indexOfMedia(btn.dataset.mediaId);
        if (i > -1) { slideIndex = i; syncCounter(); }
      } else {
        showMedia(btn.dataset.src, btn.dataset.srcset, btn.getAttribute('aria-label'));
        if (mainBox) mainBox.classList.remove('is-video');
        /* If this thumbnail is one of the product's images, the arrows carry on from it */
        const i = indexOfMedia(btn.dataset.mediaId);
        if (i > -1) { slideIndex = i; syncCounter(); }
      }
      setActiveThumb(btn);
    })
  );

  /* ---- Price, button, availability ---- */
  const priceHtml = (v) =>
    (v.compare ? `<s class="bprod__price-was">${v.compare}</s>` : '') +
    `<span class="bprod__price-now">${v.price}</span>` +
    (showCode ? `<span class="bprod__price-code">${code}</span>` : '');

  /* "Add to cart - $49.98": price x quantity on the button ("Show the price on Add to cart") */
  const showAtcPrice = !!atc && atc.dataset.showPrice === 'true';
  const moneyFormat = (atc && atc.dataset.moneyFormat) || '${{amount}}';
  const formatMoney = (cents) => {
    const m = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const group = (n, dec, t, d) => {
      const parts = (n / 100).toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return parts.join(d);
    };
    let value;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': value = group(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = group(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = group(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = group(cents, 2, "'", '.'); break;
      default: value = group(cents, 2, ',', '.');
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  };
  const qtyInput = root.querySelector('[data-bprod-qty-input]');
  const initialId = idInput && idInput.value;
  let currentCents = (variants.find((v) => String(v.id) === String(initialId)) || {}).cents;
  let buttonState = atc && atc.getAttribute('aria-disabled') !== 'true' && initialId ? 'add' : null;
  const addLabel = () => {
    const base = atc.dataset.labelAdd;
    if (!showAtcPrice || typeof currentCents !== 'number') return base;
    const q = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    return `${base} - ${formatMoney(currentCents * q)}`;
  };
  const refreshAtcPrice = () => {
    if (buttonState === 'add' && atcText) atcText.textContent = addLabel();
  };

  const setButton = (state) => {
    if (!atc) return;
    buttonState = state;
    const label = state === 'add' ? addLabel()
      : state === 'sold' ? atc.dataset.labelSold
      : state === 'choose' ? atc.dataset.labelChoose
      : atc.dataset.labelUnavailable;
    if (atcText) atcText.textContent = label;
    /* "Choose a color" stays pressable: pressing it points at the colours */
    if (state === 'add' || state === 'choose') atc.removeAttribute('aria-disabled'); else atc.setAttribute('aria-disabled', 'true');
  };
  const colorPicked = () => !colorsBox || !!colorsBox.querySelector('input:checked');

  /* Don't send the form without a colour (or for a sold-out colour). Runs before
     the theme's product-form handler, which listens on the form itself. */
  const form = root.querySelector('form.bprod__form');
  root.addEventListener('submit', (e) => {
    if (e.target !== form) return;
    if (!colorPicked() || !idInput.value) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (colorsBox) {
        colorsBox.classList.remove('is-prompt');
        void colorsBox.offsetWidth;
        colorsBox.classList.add('is-prompt');
        if (window.matchMedia('(max-width: 749px)').matches && colorToggle) {
          colorsBox.classList.add('is-open');
          colorToggle.setAttribute('aria-expanded', 'true');
        }
        colorsBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const firstTile = colorsBox.querySelector('input[type="radio"]');
        if (firstTile) firstTile.focus({ preventScroll: true });
      }
    } else if (atc && atc.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  /* Dim option values that have no available variant with the other current choices
     (an option that isn't picked yet, like the colour at first, counts as "any") */
  const anyAvailable = (opts) =>
    variants.some((v) => v.available && v.options.every((o, i) => opts[i] == null || o === opts[i]));
  const markAvailability = () => {
    const current = selectedOptions();
    optionSets.forEach((set) => {
      const index = Number(set.dataset.bprodOption);
      set.querySelectorAll('input[type="radio"]').forEach((input) => {
        const test = [...current];
        test[index] = input.value;
        input.classList.toggle('is-unavailable', !anyAvailable(test));
      });
    });
  };

  const update = () => {
    const opts = selectedOptions();
    optionSets.forEach((set) => {
      const label = set.querySelector('[data-bprod-selected]');
      const checked = set.querySelector('input:checked');
      if (label && checked) label.textContent = checked.value;
    });
    const chip = root.querySelector('[data-bprod-chip]');
    const checkedColor = colorsBox && colorsBox.querySelector('input:checked');
    if (chip && checkedColor) chip.style.background = checkedColor.dataset.hex;
    if (colorsBox && checkedColor) colorsBox.classList.remove('bprod__colors--none', 'is-prompt');

    markAvailability();
    if (!colorPicked()) { setButton('choose'); return; }
    const v = findVariant(opts);
    if (!v) { setButton('unavailable'); return; }

    idInput.value = v.id;
    currentCents = v.cents;
    if (planInput) {
      planInput.value = v.plan || '';
      planInput.disabled = !v.plan;
    }
    priceEls.forEach((el) => { el.innerHTML = priceHtml(v); });
    setButton(v.available ? 'add' : 'sold');

    if (v.src) {
      const first = root.querySelector('[data-bprod-thumb-variant]');
      if (first) {
        first.dataset.src = v.src;
        first.dataset.srcset = v.srcset || '';
        if (v.media) first.dataset.mediaId = String(v.media);
        const img = first.querySelector('img');
        if (img) { img.src = v.src; img.removeAttribute('srcset'); }
        setActiveThumb(first);
      }
      showMedia(v.src, v.srcset, v.alt);
      if (mainBox) mainBox.classList.remove('is-video');
      /* The arrows carry on from the colour's image */
      const i = indexOfMedia(v.media);
      if (i > -1) { slideIndex = i; syncCounter(); }
      if (!first) setActiveThumb(thumbForMedia(v.media));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url.toString());
    root.dispatchEvent(new CustomEvent('blinq:variant-change', { bubbles: true, detail: { variant: v } }));
  };

  optionSets.forEach((set) => set.addEventListener('change', update));
  markAvailability();

  /* ---- Phones: the colour row opens the swatches ---- */
  if (colorToggle && colorsBox) {
    colorToggle.addEventListener('click', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const open = !colorsBox.classList.contains('is-open');
      colorsBox.classList.toggle('is-open', open);
      colorToggle.setAttribute('aria-expanded', String(open));
    });
    colorsBox.addEventListener('change', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      colorsBox.classList.remove('is-open');
      colorToggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* ---- Review prompt: card above the REVIEWS.io widget ---- */
  const promptTpl = root.querySelector('template[data-bprod-review-prompt]');
  if (promptTpl) {
    const mount = () => {
      const widget = root.querySelector('.reviews-io-polaris-widget');
      if (!widget) return false;
      if (widget.dataset.blinqPrompt) return true;
      widget.dataset.blinqPrompt = 'true';
      const card = promptTpl.content.firstElementChild.cloneNode(true);
      widget.parentNode.insertBefore(card, widget);
      root.classList.add('bprod--review-prompt');
      /* Open the app's form: its header "Write Review" button, or the empty-state link */
      card.querySelector('[data-bprod-review-open]').addEventListener('click', () => {
        const appBtn =
          widget.querySelector('.ElementsWidget__header .R-Button--primary:not(.R-Button--submit)') ||
          widget.querySelector('.ElementsWidget__list [role="button"]');
        if (!appBtn) return;
        appBtn.click();
        const form = widget.querySelector('.ElementsWidget__form');
        (form || widget).scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      /* Hide the card while the form is open; show it again after Cancel / Submit */
      new MutationObserver(() => {
        card.hidden = !!widget.querySelector('.ElementsWidget__form.isActive');
      }).observe(widget, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      return true;
    };
    if (!mount()) {
      const mo = new MutationObserver(() => { if (mount()) mo.disconnect(); });
      mo.observe(root, { childList: true, subtree: true });
    }
  }

  /* ---- Quantity ---- */
  const qty = root.querySelector('[data-bprod-qty-input]');
  root.querySelectorAll('[data-bprod-qty]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const next = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(btn.dataset.bprodQty));
      qty.value = next;
      refreshAtcPrice();
    })
  );
  if (qty) {
    qty.addEventListener('input', refreshAtcPrice);
    qty.addEventListener('change', refreshAtcPrice);
  }
  };

  const initAll = (scope) => (scope || document).querySelectorAll('[data-bprod]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  /* Theme editor: a section re-render replaces its HTML, so set the new copy up */
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();